import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface SparklineCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  data: number[];
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
  color?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const SparklineCard: React.FC<SparklineCardProps> = ({
  title,
  value,
  subtitle,
  data,
  trend = 'stable',
  trendValue = 0,
  color = '#6B1786',
  icon,
  onClick,
}) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    return {
      grid: {
        left: 0,
        right: 0,
        top: 5,
        bottom: 5,
      },
      xAxis: {
        type: 'category',
        show: false,
        data: data.map((_, i) => i),
      },
      yAxis: {
        type: 'value',
        show: false,
        min: Math.min(...data) * 0.9,
        max: Math.max(...data) * 1.1,
      },
      series: [{
        type: 'line',
        data: data,
        smooth: true,
        symbol: 'none',
        lineStyle: {
          color: color,
          width: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: color + '40' },
              { offset: 1, color: color + '05' },
            ],
          },
        },
      }],
      animation: true,
      animationDuration: 500,
    };
  }, [data, color]);

  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4" />;
      case 'down': return <TrendingDown className="w-4 h-4" />;
      default: return <Minus className="w-4 h-4" />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-green-600 bg-green-50';
      case 'down': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div 
      className={`bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon && (
            <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
              {icon}
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
        
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTrendColor()}`}>
          {getTrendIcon()}
          <span>{Math.abs(trendValue).toFixed(1)}%</span>
        </div>
      </div>

      {subtitle && (
        <p className="text-xs text-gray-500 mb-2">{subtitle}</p>
      )}

      <div className="h-12">
        {data.length > 0 && (
          <ReactECharts 
            option={option} 
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        )}
      </div>
    </div>
  );
};

export default SparklineCard;
